import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useAuth }   from '../../context/AuthContext';
import * as chartApi from '../../services/chartApi';
import { listDraftMetas } from '../../services/draftStorage';
import type { ChartType } from '../../types';
import { ChartSEOMeta, ChartSEOTextBlock } from './ChartSEOMeta';
import { getChartEntry, CHART_REGISTRY, isEntryActive } from '../../config/chartRegistry';

const IS_PROD = window.location.hostname !== 'localhost'
  && window.location.hostname !== '127.0.0.1';

// Lazy page imports — static strings required for code splitting.
// Adding a new chart: add one entry here. Tabs + render are automatic.
const PAGE_COMPONENTS: Record<string, React.LazyExoticComponent<React.ComponentType>> = {
  'bar':            lazy(() => import('./charts/BarChartPage')),
  'bar-horizontal': lazy(() => import('./charts/BarHorizontalChartPage')),
  'bar-stacked':    lazy(() => import('./charts/BarStackedChartPage')),
  'bar-grouped':    lazy(() => import('./charts/BarGroupedChartPage')),
  'line':           lazy(() => import('./charts/LineChartPage')),
  'pie':            lazy(() => import('./charts/PieChartPage')),
  'heatmap':        lazy(() => import('./charts/HeatMapPage')),
  'scatter':        lazy(() => import('./charts/ScatterChartPage')),
  'bar-race':       lazy(() => import('./charts/BarRaceChartPage')),
};

// Tabs derived from CHART_REGISTRY — respects feature flags and registry order.
// Adding a new ChartEntry to CHART_REGISTRY = tab appears automatically.
const CHART_TABS = CHART_REGISTRY
  .filter(e => isEntryActive(e) && PAGE_COMPONENTS[e.chartType])
  .map(e => ({ type: e.chartType as ChartType, label: e.label }));

const ga = (event: string, params?: Record<string, string>) => {
  if (IS_PROD && (window as any).gtag) // justified: optional window.gtag analytics
    (window as any).gtag('event', event, { event_category: 'Studio', source: 'homepage', ...params }); // justified: optional window.gtag analytics
};

interface HomeChartStudioProps {
  initialType?: ChartType;
}

const HomeChartStudio: React.FC<HomeChartStudioProps> = ({ initialType = 'bar' }) => {
  const [activeType, setActiveType] = useState<ChartType>(initialType);
  const { currentUser } = useAuth() as { currentUser: { uid: string } | null };
  const navigate = useNavigate();

  useEffect(() => {
    ga('chart_studio_opened',  { initial_chart_type: initialType });
    ga('chart_viewed',         { chart_type: initialType });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Abandoned signal — fire-and-forget on mount ───────────────────────────
  // For each chart type that has a saved Firestore chart and hasn't been opened
  // in 7+ days, mark signals.abandoned = true. This is a soft negative training
  // signal — user started but didn't finish. Never blocks the UI.
  useEffect(() => {
    if (!currentUser) return;
    const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
    const cutoff = Date.now() - SEVEN_DAYS_MS;

    chartApi.listCharts(currentUser.uid).then(charts => {
      charts.forEach(c => {
        // Only mark as abandoned if: not exported, not recently updated, not already marked
        if (!c.updatedAt || c.updatedAt > cutoff) return;
        // We can only mark from the meta we have — check updatedAt age
        chartApi.markAbandoned(currentUser.uid, c.chartId).catch(() => {/* ignore */});
      });
    }).catch(() => {/* ignore — non-critical */});
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = (type: ChartType) => {
    if (type === activeType) return;
    ga('chart_studio_tab_selected', { chart_type: type, previous_chart_type: activeType });
    ga('chart_viewed', { chart_type: type });
    setActiveType(type);
    const entry = getChartEntry(type);
    if (entry?.seo) navigate(`/${entry.seo.slug}`, { replace: true });
  };

  // LoadingSpinner is a JS component — typed via React.ComponentType<object>
  const Spinner = LoadingSpinner as React.ComponentType<object>;

  return (
    <>
    <ChartSEOMeta chartType={activeType} />
    <section id="create" style={{ background: '#edeef0', paddingTop: 0, paddingBottom: 0 }}>

      {/* Studio header bar */}
      <div style={{ background: '#fff', borderBottom: '2px solid #e8eaed', flexShrink: 0 }}>

        {/* Row 1 — branding */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '22px 28px 8px', gap: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 18, fontWeight: 900, color: '#111827', letterSpacing: '-0.025em', lineHeight: 1 }}>
              Chart<span style={{ color: 'var(--c2v-primary)' }}>2</span>Video
            </span>
            <span style={{
              fontSize: 10, fontWeight: 700, color: 'var(--c2v-primary)',
              background: 'var(--c2v-primary-light)', borderRadius: 4, padding: '2px 6px',
              letterSpacing: '.06em', textTransform: 'uppercase',
            }}>Studio</span>
          </div>
          <span style={{ fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap' }}>
            Animate → Export MP4 or JPG
          </span>
        </div>

        {/* Row 2 — tabs */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 4, padding: '2px 28px 20px', flexWrap: 'wrap' }}>
          {CHART_TABS.map(({ type, label }) => (
            <button
              key={type}
              onClick={() => handleTabChange(type)}
              style={{
                padding: '7px 24px', borderRadius: 8, fontSize: 14, fontWeight: 600,
                border: activeType === type ? '1.5px solid var(--c2v-primary)' : '1.5px solid transparent',
                cursor: 'pointer', transition: 'all 0.15s',
                background: activeType === type ? 'var(--c2v-primary)' : '#f4f4f6',
                color:      activeType === type ? '#fff' : '#6b7280',
                letterSpacing: '.01em',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Chart pages */}
      <Suspense fallback={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400 }}>
          <Spinner />
        </div>
      }>
        {(() => {
          const ActivePage = PAGE_COMPONENTS[activeType];
          return ActivePage ? <ActivePage /> : null;
        })()}
      </Suspense>
    </section>
    <ChartSEOTextBlock chartType={activeType} />
    </>
  );
};

export default HomeChartStudio;